// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack — Integrations Page
// Route: /integrations
// apps/web/src/routes/integrations.tsx
//
// Collector API keys for headless machine-to-machine ingestion (the local
// ClaudeCodeCollector is today's sole consumer). Designed to grow — future
// integrations (webhooks, outbound API tokens) will land as sibling
// GlassPanels on this same route.
//
// Step-up MFA (design doc §7c) is planned for Wave 4. Until that lands,
// mint / revoke / rotate are JWT-authenticated only; the server will
// require fresh MFA once [RequireFreshMfa] is wired up.
// ═══════════════════════════════════════════════════════════════════════════

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../components/engine/tokens';
import EnginePageShell, {
  GlassPanel,
  SectionLabel,
  EngineTooltip,
  MetricCard,
  EmptyState,
  LoadingShimmer,
  StatusBadge,
  PulseDot,
} from '../components/engine/EnginePageShell';
import { useCounter } from '../components/engine/hooks';
import {
  useCollectorKeys,
  useSubscriptionLimits,
  useCollectorKeyMutations,
} from '../lib/api/collector-keys';
import type {
  CollectorKeySummary,
  MintedCollectorKey,
} from '../lib/api/types-collector-keys';
import { GenerateKeyModal } from '../components/collector-keys/GenerateKeyModal';
import { TokenRevealModal } from '../components/collector-keys/TokenRevealModal';

export const Route = createFileRoute('/integrations')({
  component: IntegrationsPage,
});

// ─── Main component ─────────────────────────────────────────────────────────

function IntegrationsPage() {
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const { data: keys,   loading: keysLoading,   refetch: refetchKeys }   = useCollectorKeys(includeRevoked);
  const { data: limits, loading: limitsLoading, refetch: refetchLimits } = useSubscriptionLimits();
  const mutations = useCollectorKeyMutations();

  const [showGenerate, setShowGenerate] = useState(false);
  const [revealKey,    setRevealKey]    = useState<MintedCollectorKey | null>(null);
  const [stepUpDone,   setStepUpDone]   = useState(false);

  // On mount, check whether we just returned from a step-up redirect
  // (@forgetrack/auth sets this flag on the return side). Show a transient
  // banner, clear the flag so a refresh doesn't re-show it.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem('ftcc_stepup_completed') === '1') {
      sessionStorage.removeItem('ftcc_stepup_completed');
      setStepUpDone(true);
      const t = setTimeout(() => setStepUpDone(false), 8000);
      return () => clearTimeout(t);
    }
  }, []);

  const activeCount = useCounter(limits?.activeKeys ?? 0, 700, 200);
  const maxCount    = useCounter(limits?.maxCollectorKeys ?? 0, 700, 280);
  const atLimit     = limits && limits.activeKeys >= limits.maxCollectorKeys;

  const onGenerated = (minted: MintedCollectorKey) => {
    setShowGenerate(false);
    setRevealKey(minted);
    void refetchKeys();
    void refetchLimits();
  };

  const onCloseReveal = () => {
    setRevealKey(null);
  };

  const onRevoke = async (id: number) => {
    const ok = await mutations.revoke(id, { reason: 'Revoked from Integrations page' });
    if (ok) {
      await refetchKeys();
      await refetchLimits();
    }
  };

  const onRotate = async (id: number) => {
    const minted = await mutations.rotate(id);
    if (minted) {
      setRevealKey(minted);
      await refetchKeys();
      await refetchLimits();
    }
  };

  return (
    <EnginePageShell
      title="Integrations"
      subtitle="MACHINE-TO-MACHINE"
      icon="🔑"
      accentColor={C.violet}
      statusLabel="SECURE"
      statusColor={C.violet}
    >
      {/* ── Step-up MFA return banner ──────────────────────────────────── */}
      <AnimatePresence>
        {stepUpDone && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            style={{
              marginBottom: 16,
              padding: '12px 16px',
              background: `${C.neon}12`,
              border: `1px solid ${C.neon}40`,
              borderRadius: 10,
              display: 'flex', alignItems: 'center', gap: 12,
            }}
          >
            <PulseDot color={C.neon} />
            <div>
              <div style={{ fontFamily: F.mono, fontSize: 11, color: C.neon, fontWeight: 600, letterSpacing: '0.08em' }}>
                IDENTITY VERIFIED
              </div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                Your sign-in is now fresh. Retry the action that required step-up MFA.
              </div>
            </div>
            <button
              onClick={() => setStepUpDone(false)}
              style={{
                marginLeft: 'auto',
                background: 'transparent', border: 'none', color: C.muted,
                fontFamily: F.mono, fontSize: 14, cursor: 'pointer', padding: 4,
              }}
              aria-label="Dismiss"
            >
              ✕
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Quota strip ────────────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 14, marginBottom: 24,
      }}>
        <EngineTooltip content={
          <div>
            Active collector API keys counted against your plan's quota.
            Revoked or expired keys don't count.
          </div>
        }>
          <div>
            <MetricCard
              label="Keys Used"
              value={limitsLoading ? '—' : `${activeCount} / ${maxCount}`}
              icon="🔑"
              color={atLimit ? C.amber : C.violet}
              subtitle={limits?.hasOverride ? 'Platform override active' : undefined}
            />
          </div>
        </EngineTooltip>

        <EngineTooltip content={
          <div>
            Your current subscription tier. Collector key quota varies by tier —
            upgrade for more keys and higher quotas across the platform.
          </div>
        }>
          <div>
            <MetricCard
              label="Plan"
              value={limits?.tier ?? '—'}
              icon="📦"
              color={C.blue}
              subtitle={
                limits && limits.maxCollectorKeysFromTier !== limits.maxCollectorKeys
                  ? `Base ${limits.maxCollectorKeysFromTier} • Effective ${limits.maxCollectorKeys}`
                  : undefined
              }
            />
          </div>
        </EngineTooltip>
      </div>

      {/* ── Collector API Keys section ─────────────────────────────────── */}
      <GlassPanel accent={C.violet} delay={0.3}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16,
        }}>
          <div>
            <SectionLabel color={C.violet}>Collector API Keys</SectionLabel>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
              Long-lived tokens for the local ClaudeCodeCollector and future
              machine-to-machine integrations. Scope-limited; revocable.
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <label style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontFamily: F.mono, fontSize: 10, color: C.muted, cursor: 'pointer',
            }}>
              <input
                type="checkbox"
                checked={includeRevoked}
                onChange={e => setIncludeRevoked(e.target.checked)}
              />
              SHOW REVOKED
            </label>
            <button
              onClick={() => setShowGenerate(true)}
              disabled={!!atLimit}
              style={{
                background: atLimit ? `${C.faint}` : `${C.violet}18`,
                border: `1px solid ${atLimit ? C.faint : `${C.violet}60`}`,
                color: atLimit ? C.dim : C.violet,
                fontFamily: F.mono, fontSize: 10, padding: '6px 14px',
                borderRadius: 6, cursor: atLimit ? 'not-allowed' : 'pointer',
                letterSpacing: '0.08em', fontWeight: 600,
              }}
              title={atLimit ? 'Revoke an existing key or upgrade your plan to generate more.' : undefined}
            >
              + GENERATE KEY
            </button>
          </div>
        </div>

        {keysLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[0, 1, 2].map(i => <LoadingShimmer key={i} height={64} />)}
          </div>
        ) : keys && keys.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {keys.map((key, i) => (
              <KeyRow
                key={key.id}
                keyRow={key}
                index={i}
                busy={mutations.loading}
                onRevoke={() => onRevoke(key.id)}
                onRotate={() => onRotate(key.id)}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            icon="🔑"
            title="No collector keys yet"
            subtitle="Generate your first key to connect the local ClaudeCodeCollector or other headless integrations."
            action={
              <button
                onClick={() => setShowGenerate(true)}
                style={{
                  background: `${C.violet}15`, border: `1px solid ${C.violet}50`,
                  color: C.violet, fontFamily: F.mono, fontSize: 11,
                  padding: '10px 22px', borderRadius: 8, cursor: 'pointer',
                }}
              >
                + GENERATE KEY
              </button>
            }
          />
        )}

        {mutations.error && (
          <div style={{
            marginTop: 14, padding: '10px 14px', borderRadius: 8,
            background: `${C.coral}15`, border: `1px solid ${C.coral}40`,
            color: C.coral, fontFamily: F.mono, fontSize: 11,
          }}>
            ✕ {mutations.error.message}
          </div>
        )}
      </GlassPanel>

      {showGenerate && (
        <GenerateKeyModal
          onClose={() => setShowGenerate(false)}
          onGenerated={onGenerated}
          mint={mutations.mint}
          loading={mutations.loading}
          error={mutations.error}
        />
      )}

      {revealKey && (
        <TokenRevealModal minted={revealKey} onClose={onCloseReveal} />
      )}
    </EnginePageShell>
  );
}

// ─── Key row ────────────────────────────────────────────────────────────────

interface KeyRowProps {
  keyRow:    CollectorKeySummary;
  index:     number;
  busy:      boolean;
  onRevoke:  () => void;
  onRotate:  () => void;
}

function KeyRow({ keyRow, index, busy, onRevoke, onRotate }: KeyRowProps) {
  const [confirmText, setConfirmText] = useState('');
  const [rotateConfirm, setRotateConfirm] = useState(false);

  const scopes = useMemo(() => {
    try { return JSON.parse(keyRow.scopesJson) as string[]; }
    catch { return []; }
  }, [keyRow.scopesJson]);

  const isRevoked = !!keyRow.revokedAt;
  const isExpiring = !isRevoked && keyRow.expiresAt && (
    new Date(keyRow.expiresAt).getTime() - Date.now() < 7 * 24 * 60 * 60 * 1000
  );

  const { label, color } = isRevoked
    ? { label: 'REVOKED', color: C.coral }
    : isExpiring
      ? { label: 'EXPIRING SOON', color: C.amber }
      : { label: 'ACTIVE', color: C.neon };

  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.3 + index * 0.06, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      style={{
        background: isRevoked ? 'rgba(0,0,0,0.3)' : C.surface,
        border: `1px solid ${isRevoked ? C.faint : `${color}22`}`,
        borderRadius: 10, padding: 14, opacity: isRevoked ? 0.55 : 1,
      }}
    >
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 12, alignItems: 'center',
      }}>
        {/* LEFT: name, prefix, metadata */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            {!isRevoked && <PulseDot color={color} />}
            <div style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>{keyRow.name}</div>
            <StatusBadge label={label} color={color} />
          </div>

          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <EngineTooltip content={
              <div>
                The public portion of the token, used for audit lookup. The
                secret portion was only shown once, when the key was generated.
              </div>
            }>
              <code style={{
                fontFamily: F.mono, fontSize: 11, color: C.muted,
                background: 'rgba(0,0,0,0.3)', padding: '2px 8px', borderRadius: 4,
                cursor: 'pointer',
              }}>
                {keyRow.maskedPrefix}
              </code>
            </EngineTooltip>

            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {scopes.map(s => (
                <span key={s} style={{
                  background: `${C.violet}12`, color: C.violet, fontFamily: F.mono, fontSize: 9,
                  padding: '2px 6px', borderRadius: 3, border: `1px solid ${C.violet}25`,
                }}>
                  {s}
                </span>
              ))}
            </div>
          </div>

          <div style={{
            display: 'flex', gap: 16, fontFamily: F.mono, fontSize: 10, color: C.dim, marginTop: 8,
          }}>
            <span>created {new Date(keyRow.createdAt).toLocaleDateString()}</span>
            {keyRow.expiresAt && (
              <span>expires {new Date(keyRow.expiresAt).toLocaleDateString()}</span>
            )}
            <span>
              {keyRow.lastUsedAt
                ? `last used ${relativeTime(keyRow.lastUsedAt)}${keyRow.lastUsedIp ? ' from ' + keyRow.lastUsedIp : ''}`
                : 'never used'}
            </span>
            <span>{keyRow.usageCount.toLocaleString()} requests</span>
          </div>

          {isRevoked && keyRow.revokedReason && (
            <div style={{ fontFamily: F.mono, fontSize: 10, color: C.coral, marginTop: 6 }}>
              revoked {relativeTime(keyRow.revokedAt!)}: {keyRow.revokedReason}
            </div>
          )}
        </div>

        {/* RIGHT: action buttons */}
        {!isRevoked && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {/* Rotate */}
            {rotateConfirm ? (
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <span style={{ fontSize: 10, color: C.amber, fontFamily: F.mono }}>
                  Old token will stop working. Sure?
                </span>
                <button
                  onClick={() => { setRotateConfirm(false); onRotate(); }}
                  disabled={busy}
                  style={actionBtn(C.amber)}
                >
                  ROTATE
                </button>
                <button
                  onClick={() => setRotateConfirm(false)}
                  style={actionBtn(C.muted, 'transparent')}
                >
                  CANCEL
                </button>
              </div>
            ) : (
              <button
                onClick={() => setRotateConfirm(true)}
                disabled={busy}
                style={actionBtn(C.blue)}
                title="Revoke and mint a new token with the same name + scopes"
              >
                🔄 ROTATE
              </button>
            )}

            {/* Revoke — type-to-confirm */}
            {confirmText !== '' ? (
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <input
                  value={confirmText === '__init__' ? '' : confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder={`type "${keyRow.name}"`}
                  autoFocus
                  style={{
                    background: 'rgba(0,0,0,0.3)',
                    border: `1px solid ${C.coral}40`,
                    borderRadius: 6,
                    padding: '5px 8px',
                    fontFamily: F.mono, fontSize: 10,
                    color: C.text, width: 180, outline: 'none',
                  }}
                />
                <button
                  onClick={() => { setConfirmText(''); onRevoke(); }}
                  disabled={busy || confirmText !== keyRow.name}
                  style={{
                    ...actionBtn(C.coral),
                    opacity: confirmText === keyRow.name ? 1 : 0.4,
                    cursor: confirmText === keyRow.name ? 'pointer' : 'not-allowed',
                  }}
                >
                  REVOKE
                </button>
                <button
                  onClick={() => setConfirmText('')}
                  style={actionBtn(C.muted, 'transparent')}
                >
                  CANCEL
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmText('__init__')}
                disabled={busy}
                style={actionBtn(C.coral)}
              >
                ✕ REVOKE
              </button>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function actionBtn(color: string, bg: string = `${color}15`): React.CSSProperties {
  return {
    background: bg, border: `1px solid ${color}45`, color: color,
    fontFamily: F.mono, fontSize: 10, padding: '5px 10px', borderRadius: 6,
    cursor: 'pointer', letterSpacing: '0.06em', fontWeight: 600,
  };
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now  = Date.now();
  const diff = now - then;
  const s = Math.round(diff / 1000);
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.round(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.round(mo / 12)}y ago`;
}
